/**
 * Event editor form state and handlers.
 * Extracted from AdminEventEditPage for reuse in section components.
 * Uses URQL for GraphQL operations with circuit breaker protection.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "urql";
import { useAuth } from "../../../../lib/auth.tsx";
import { getAuthHeaders } from "../../../../lib/api-headers";
import { config } from "../../../../config.ts";
import { safeParseResponseJson } from "../../../../lib/json-utils";
import {
  formatDateForInputBangkok,
  datetimeLocalToBangkokISO,
} from "../../../../lib/bangkok-time.ts";
import {
  haptic,
  safeShowAlert,
  safeShowConfirm,
} from "../../../../lib/telegram.ts";
import { getErrorMessage } from "../../../../lib/graphql.ts";
import {
  type AdminEvent,
  type EventStatus,
  type EventVisibility,
  type Gender,
  type Category,
  type TicketType,
  EVENT_STATUS_CONFIG,
  EVENT_VISIBILITY_CONFIG,
  GENDER_CONFIG,
} from "../types.ts";
import type { SeriesConfig } from "../components/SeriesBuilder.tsx";
import {
  useUpdateEvent,
  useDeleteEvent,
  useDuplicateEvent,
} from "./useEventMutations.ts";
import { safeJsonParse } from "../../../../lib/json-utils.ts";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const AdminEventFullDocument = `
  query AdminEventFull($id: ID!) {
    adminEvent(id: $id) {
      id
      title
      subtitle
      description
      venue
      address
      mapsUrl
      startsAt
      endsAt
      imageUrl
      bannerImageUrl
      videoUrl
      streamYoutubeUrl
      streamVkUrl
      telegramChatUrl
      inviteText
      slogan
      program
      status
      visibility
      publishAt
      featured
      genderRestriction
      minAge
      maxAge
      maxAttendees
      requiresRsvpConfirmation
      homeGroup { id }
      seriesId
      isActive
      createdAt
      categories {
        id
        name
        color
      }
      organizers {
        id
        ministry { id name }
        account { id firstName lastName username }
        role
        canEdit
        canManageRsvp
        canManageTickets
        receiveNotifications
      }
      ticketTypes {
        id
        name
        description
        maxQuantity
        soldCount
        isActive
        prices { id currency price }
        price
        currency
        fees {
          feeConfiguration {
            id
            type
            prices { currency amount }
          }
        }
      }
    }
    categories {
      id
      code
      name
      color
      parent { id }
    }
    feeConfigurations {
      id
      name
      type
      calculation
      prices { id currency amount }
    }
  }
`;

const AdminHomeGroupsDocument = `
  query AdminHomeGroupsForSelect {
    adminHomeGroups {
      id
      name
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

export interface FeeConfiguration {
  id: string;
  name: string;
  type: string;
  calculation: string;
  prices: { id: string; currency: string; amount: string }[];
}

export type EditingField =
  | "subtitle"
  | "address"
  | "slogan"
  | "description"
  | "inviteText"
  | "program"
  | null;

interface AdminEventQueryResult {
  adminEvent: AdminEvent | null;
  categories: Category[];
  feeConfigurations: FeeConfiguration[];
}

interface HomeGroupsQueryResult {
  adminHomeGroups: { id: string; name: string }[];
}

// =============================================================================
// HOOK
// =============================================================================

export function useEventEditor() {
  const { eventId } = useParams<{ eventId: string }>();
  const { account } = useAuth();

  // URQL queries
  const [eventResult, reexecuteEventQuery] = useQuery<AdminEventQueryResult>({
    query: AdminEventFullDocument,
    variables: { id: eventId },
    pause: !eventId || !account,
  });

  const [homeGroupsResult] = useQuery<HomeGroupsQueryResult>({
    query: AdminHomeGroupsDocument,
    pause: !account,
  });

  // URQL mutations
  const { updateEvent, isLoading: isUpdating } = useUpdateEvent();
  const { deleteEvent, isLoading: isDeletingMutation } = useDeleteEvent();
  const { duplicateEvent, isLoading: isDuplicatingMutation } =
    useDuplicateEvent();

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [event, setEvent] = useState<AdminEvent | null>(null);
  const [allCategories, setAllCategories] = useState<Category[]>([]);

  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");
  const [venue, setVenue] = useState("");
  const [address, setAddress] = useState("");
  const [mapsUrl, setMapsUrl] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [maxAttendees, setMaxAttendees] = useState<number | null>(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [rrule, setRrule] = useState("");
  const [seriesConfig, setSeriesConfig] = useState<SeriesConfig | null>(null);

  const [imageUrl, setImageUrl] = useState("");
  const [bannerImageUrl, setBannerImageUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [streamYoutubeUrl, setStreamYoutubeUrl] = useState("");
  const [streamVkUrl, setStreamVkUrl] = useState("");
  const [telegramChatUrl, setTelegramChatUrl] = useState("");

  const [inviteText, setInviteText] = useState("");
  const [slogan, setSlogan] = useState("");
  const [program, setProgram] = useState("");

  const [status, setStatus] = useState<EventStatus>("DRAFT");
  const [publishAt, setPublishAt] = useState("");
  const [visibility, setVisibility] = useState<EventVisibility>("PUBLIC");
  const [featured, setFeatured] = useState(false);
  const [genderRestriction, setGenderRestriction] = useState<Gender>("ANY");
  const [minAge, setMinAge] = useState<number | null>(null);
  const [maxAge, setMaxAge] = useState<number | null>(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [homeGroupId, setHomeGroupId] = useState<string | null>(null);
  const [requiresRsvpConfirmation, setRequiresRsvpConfirmation] =
    useState(false);

  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingBannerImage, setIsUploadingBannerImage] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [imageCacheBust, setImageCacheBust] = useState(0);
  const [bannerCacheBust, setBannerCacheBust] = useState(0);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const videoXhrRef = useRef<XMLHttpRequest | null>(null);

  const [editingTicketType, setEditingTicketType] = useState<
    TicketType | null | "new"
  >(null);
  const [serviceFeeConfigId, setServiceFeeConfigId] = useState<string | null>(
    null
  );
  const [serviceFeePrices, setServiceFeePrices] = useState<
    Record<string, string>
  >({
    THB: "",
    RUB: "",
  });

  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const [showVisibilityPicker, setShowVisibilityPicker] = useState(false);
  const [showGenderPicker, setShowGenderPicker] = useState(false);
  const [showHomeGroupPicker, setShowHomeGroupPicker] = useState(false);
  const [editingField, setEditingField] = useState<EditingField>(null);

  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const initialFormRef = useRef<string>("");

  // Derived state from URQL
  const isLoading = eventResult.fetching;
  const error = eventResult.error?.message ?? null;
  const homeGroups = homeGroupsResult.data?.adminHomeGroups ?? [];

  const resetForm = useCallback(() => {
    setTitle("");
    setSubtitle("");
    setDescription("");
    setVenue("");
    setAddress("");
    setMapsUrl("");
    setStartsAt("");
    setEndsAt("");
    setMaxAttendees(null);
    setIsRecurring(false);
    setRrule("");
    setSeriesConfig(null);
    setImageUrl("");
    setBannerImageUrl("");
    setVideoUrl("");
    setStreamYoutubeUrl("");
    setStreamVkUrl("");
    setTelegramChatUrl("");
    setInviteText("");
    setSlogan("");
    setProgram("");
    setStatus("DRAFT");
    setPublishAt("");
    setVisibility("PUBLIC");
    setFeatured(false);
    setGenderRestriction("ANY");
    setMinAge(null);
    setMaxAge(null);
    setSelectedCategoryIds([]);
    setHomeGroupId(null);
    setRequiresRsvpConfirmation(false);
    setEditingTicketType(null);
    setServiceFeeConfigId(null);
    setServiceFeePrices({ THB: "", RUB: "" });
  }, []);

  useEffect(() => {
    setEvent(null);
    resetForm();
  }, [eventId, resetForm]);

  // Cleanup XHR on unmount to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      if (videoXhrRef.current) {
        videoXhrRef.current.abort();
        videoXhrRef.current = null;
      }
    };
  }, []);

  // Sync form state when URQL data changes
  useEffect(() => {
    if (!eventResult.data?.adminEvent) return;

    const e = eventResult.data.adminEvent;
    setEvent(e);
    setAllCategories(eventResult.data.categories || []);

    setTitle(e.title);
    setSubtitle(e.subtitle ?? "");
    setDescription(e.description ?? "");
    setVenue(e.venue ?? "");
    setAddress(e.address ?? "");
    setMapsUrl(e.mapsUrl ?? "");
    setStartsAt(formatDateForInputBangkok(e.startsAt));
    setEndsAt(e.endsAt ? formatDateForInputBangkok(e.endsAt) : "");
    setMaxAttendees(e.maxAttendees);
    setImageUrl(e.imageUrl ?? "");
    setBannerImageUrl(e.bannerImageUrl ?? "");
    setVideoUrl(e.videoUrl ?? "");
    setStreamYoutubeUrl(e.streamYoutubeUrl ?? "");
    setStreamVkUrl(e.streamVkUrl ?? "");
    setTelegramChatUrl(e.telegramChatUrl ?? "");
    setInviteText(e.inviteText ?? "");
    setSlogan(e.slogan ?? "");
    setProgram(e.program ?? "");
    setStatus(e.status);
    setPublishAt(e.publishAt ? formatDateForInputBangkok(e.publishAt) : "");
    setVisibility(e.visibility);
    setFeatured(e.featured);
    setGenderRestriction(e.genderRestriction);
    setMinAge(e.minAge);
    setMaxAge(e.maxAge);
    setSelectedCategoryIds(e.categories.map((c) => c.id));
    setHomeGroupId(e.homeGroup?.id ?? null);
    setRequiresRsvpConfirmation(e.requiresRsvpConfirmation ?? false);

    const serviceFee = eventResult.data.feeConfigurations?.find(
      (f) => f.type === "SERVICE" && f.calculation === "FIXED"
    );
    if (serviceFee) {
      setServiceFeeConfigId(serviceFee.id);
      const prices: Record<string, string> = { THB: "", RUB: "" };
      for (const p of serviceFee.prices || []) {
        prices[p.currency] = String(p.amount ?? "");
      }
      setServiceFeePrices(prices);
    }
    initialFormRef.current = JSON.stringify({
      title: e.title,
      startsAt: formatDateForInputBangkok(e.startsAt),
    });
  }, [eventResult.data]);

  const loadEvent = useCallback(() => {
    reexecuteEventQuery({ requestPolicy: "network-only" });
  }, [reexecuteEventQuery]);

  const handleSave = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;
      if (!title.trim()) {
        if (!silent) safeShowAlert("Введите название мероприятия");
        return;
      }
      if (!startsAt) {
        if (!silent) safeShowAlert("Укажите дату начала");
        return;
      }
      if (!eventId) return;

      setIsSaving(true);
      try {
        await updateEvent(eventId, {
          title: title.trim(),
          subtitle: subtitle.trim() || null,
          description: description.trim() || null,
          venue: venue.trim() || null,
          address: address.trim() || null,
          mapsUrl: mapsUrl.trim() || null,
          startsAt: datetimeLocalToBangkokISO(startsAt),
          endsAt: endsAt ? datetimeLocalToBangkokISO(endsAt) : null,
          maxAttendees: maxAttendees,
          imageUrl: imageUrl || null,
          bannerImageUrl: bannerImageUrl || null,
          videoUrl: videoUrl || null,
          streamYoutubeUrl: streamYoutubeUrl.trim() || null,
          streamVkUrl: streamVkUrl.trim() || null,
          telegramChatUrl: telegramChatUrl.trim() || null,
          inviteText: inviteText.trim() || null,
          slogan: slogan.trim() || null,
          program: program.trim() || null,
          status,
          publishAt: publishAt ? datetimeLocalToBangkokISO(publishAt) : null,
          visibility,
          featured,
          genderRestriction,
          minAge: minAge,
          maxAge: maxAge,
          categoryIds: selectedCategoryIds,
          homeGroupId: homeGroupId ?? null,
          requiresRsvpConfirmation,
          isActive: status === "PUBLISHED",
        });
        setLastSavedAt(Date.now());
        initialFormRef.current = JSON.stringify({
          title: title.trim(),
          startsAt,
        });
        if (!silent) {
          haptic("success");
          safeShowAlert("Сохранено");
        }
      } catch (err) {
        if (!silent) {
          haptic("error");
          safeShowAlert(getErrorMessage(err, "Не удалось сохранить"));
        }
      } finally {
        setIsSaving(false);
      }
    },
    [
      eventId,
      title,
      subtitle,
      description,
      venue,
      address,
      mapsUrl,
      startsAt,
      endsAt,
      maxAttendees,
      imageUrl,
      bannerImageUrl,
      videoUrl,
      streamYoutubeUrl,
      streamVkUrl,
      telegramChatUrl,
      inviteText,
      slogan,
      program,
      status,
      publishAt,
      visibility,
      featured,
      genderRestriction,
      minAge,
      maxAge,
      selectedCategoryIds,
      homeGroupId,
      requiresRsvpConfirmation,
      updateEvent,
    ]
  );

  const AUTO_SAVE_INTERVAL_MS = 30_000;
  useEffect(() => {
    if (!eventId || status !== "DRAFT") return;
    const intervalId = setInterval(() => {
      const snapshot = JSON.stringify({ title, startsAt });
      if (
        initialFormRef.current !== "" &&
        snapshot !== initialFormRef.current
      ) {
        void handleSave({ silent: true });
      }
    }, AUTO_SAVE_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [eventId, status, title, startsAt, handleSave]);

  const handleDelete = useCallback(
    (navigate: (path: string) => void) => {
      safeShowConfirm(
        "Удалить мероприятие? Это действие нельзя отменить.",
        async (ok) => {
          if (!ok || !eventId) return;
          setIsDeleting(true);
          try {
            await deleteEvent(eventId);
            haptic("success");
            navigate("/admin/events");
          } catch (err) {
            haptic("error");
            safeShowAlert(getErrorMessage(err, "Не удалось удалить"));
          } finally {
            setIsDeleting(false);
          }
        }
      );
    },
    [eventId, deleteEvent]
  );

  const handleDuplicate = useCallback(
    async (navigate: (path: string) => void) => {
      if (!eventId) return;
      setIsDuplicating(true);
      try {
        const result = await duplicateEvent(eventId);
        haptic("success");
        navigate(`/admin/events/${result.id}`);
      } catch (err) {
        haptic("error");
        safeShowAlert(getErrorMessage(err, "Не удалось скопировать"));
      } finally {
        setIsDuplicating(false);
      }
    },
    [eventId, duplicateEvent]
  );

  const handleImageUpload = useCallback(
    async (file: File) => {
      if (!eventId) return;
      setIsUploadingImage(true);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const response = await fetch(
          `${config.apiUrl}/upload/event-image/${eventId}`,
          { method: "POST", headers: getAuthHeaders(), body: formData }
        );
        if (!response.ok) {
          const data = await safeParseResponseJson<{ error?: string }>(response, "image upload error");
          throw new Error(data.error || "Ошибка загрузки");
        }
        const result = await response.json() as { url: string };
        setImageUrl(result.url);
        setImageCacheBust(Date.now());
        haptic("success");
      } catch (err) {
        haptic("error");
        safeShowAlert(getErrorMessage(err, "Ошибка загрузки"));
      } finally {
        setIsUploadingImage(false);
        if (imageInputRef.current) imageInputRef.current.value = "";
      }
    },
    [eventId]
  );

  const handleBannerImageUpload = useCallback(
    async (file: File) => {
      if (!eventId) return;
      setIsUploadingBannerImage(true);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const response = await fetch(
          `${config.apiUrl}/upload/event-banner/${eventId}`,
          { method: "POST", headers: getAuthHeaders(), body: formData }
        );
        if (!response.ok) {
          const data = await safeParseResponseJson<{ error?: string }>(response, "banner upload error");
          throw new Error(data.error || "Ошибка загрузки");
        }
        const result = await response.json() as { url: string };
        setBannerImageUrl(result.url);
        setBannerCacheBust(Date.now());
        haptic("success");
      } catch (err) {
        haptic("error");
        safeShowAlert(getErrorMessage(err, "Ошибка загрузки"));
      } finally {
        setIsUploadingBannerImage(false);
        if (bannerInputRef.current) bannerInputRef.current.value = "";
      }
    },
    [eventId]
  );

  const handleVideoUpload = useCallback(
    (file: File) => {
      if (!eventId) return;

      // Abort any existing upload
      if (videoXhrRef.current) {
        videoXhrRef.current.abort();
      }

      setIsUploadingVideo(true);
      setVideoUploadProgress(0);
      const formData = new FormData();
      formData.append("file", file);
      const xhr = new XMLHttpRequest();
      videoXhrRef.current = xhr;

      const url = `${config.apiUrl}/upload/event-video/${eventId}`;
      const headers = getAuthHeaders();

      const cleanup = () => {
        if (videoXhrRef.current === xhr) {
          videoXhrRef.current = null;
        }
        setIsUploadingVideo(false);
        setVideoUploadProgress(0);
        if (videoInputRef.current) videoInputRef.current.value = "";
      };

      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          setVideoUploadProgress(Math.round((e.loaded / e.total) * 100));
        } else {
          setVideoUploadProgress((prev) => Math.min(prev + 10, 90));
        }
      });
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const result = safeJsonParse<{ url?: string } | null>(xhr.responseText, null);
          if (result?.url) {
            setVideoUrl(result.url);
            setVideoUploadProgress(100);
            haptic("success");
          } else {
            haptic("error");
            safeShowAlert("Ошибка ответа сервера");
          }
        } else {
          const data = safeJsonParse<{ error?: string } | null>(xhr.responseText, null);
          safeShowAlert(data?.error || "Ошибка загрузки");
          haptic("error");
        }
        cleanup();
      });
      xhr.addEventListener("error", () => {
        haptic("error");
        safeShowAlert("Ошибка сети");
        cleanup();
      });
      xhr.addEventListener("abort", () => {
        cleanup();
      });
      xhr.open("POST", url);
      for (const [key, value] of Object.entries(headers)) {
        xhr.setRequestHeader(key, value);
      }
      xhr.send(formData);
    },
    [eventId]
  );

  const toggleCategory = useCallback((categoryId: string) => {
    haptic("light");
    setSelectedCategoryIds((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  }, []);

  const getEditorConfig = useCallback(() => {
    switch (editingField) {
      case "subtitle":
        return {
          title: "Подзаголовок",
          value: subtitle,
          onSave: setSubtitle,
          placeholder: "Краткое дополнение к названию...",
        };
      case "address":
        return {
          title: "Адрес",
          value: address,
          onSave: setAddress,
          placeholder: "Полный адрес места проведения...",
        };
      case "slogan":
        return {
          title: "Слоган",
          value: slogan,
          onSave: setSlogan,
          placeholder: "Короткая запоминающаяся фраза...",
        };
      case "description":
        return {
          title: "Полное описание",
          value: description,
          onSave: setDescription,
          placeholder: "Подробное описание мероприятия...",
        };
      case "inviteText":
        return {
          title: "Текст приглашения",
          value: inviteText,
          onSave: setInviteText,
          placeholder: "Приходи сам и приводи друзей!",
        };
      case "program":
        return {
          title: "Программа",
          value: program,
          onSave: setProgram,
          placeholder: "10:00 — Начало\n10:30 — Поклонение\n11:00 — Проповедь",
        };
      default:
        return null;
    }
  }, [
    editingField,
    subtitle,
    address,
    slogan,
    description,
    inviteText,
    program,
  ]);

  const formatDisplayDate = useCallback((dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
  }, []);

  const progressFilled = (title.trim() ? 1 : 0) + (startsAt ? 1 : 0);
  const progressTotal = 2;

  const hasUnsavedChanges =
    initialFormRef.current !== "" &&
    JSON.stringify({ title, startsAt }) !== initialFormRef.current;

  return {
    eventId,
    event,
    allCategories,
    isLoading,
    error,
    isSaving: isSaving || isUpdating,
    isDeleting: isDeleting || isDeletingMutation,
    isDuplicating: isDuplicating || isDuplicatingMutation,
    loadEvent,
    handleSave,
    handleDelete,
    handleDuplicate,
    handleImageUpload,
    handleBannerImageUpload,
    handleVideoUpload,
    toggleCategory,
    getEditorConfig,
    formatDisplayDate,
    progressFilled,
    progressTotal,
    hasUnsavedChanges,
    lastSavedAt,
    imageInputRef,
    bannerInputRef,
    videoInputRef,
    title,
    setTitle,
    subtitle,
    setSubtitle,
    description,
    setDescription,
    venue,
    setVenue,
    address,
    setAddress,
    mapsUrl,
    setMapsUrl,
    startsAt,
    setStartsAt,
    endsAt,
    setEndsAt,
    maxAttendees,
    setMaxAttendees,
    isRecurring,
    setIsRecurring,
    rrule,
    setRrule,
    seriesConfig,
    setSeriesConfig,
    imageUrl,
    setImageUrl,
    bannerImageUrl,
    setBannerImageUrl,
    videoUrl,
    setVideoUrl,
    streamYoutubeUrl,
    setStreamYoutubeUrl,
    streamVkUrl,
    setStreamVkUrl,
    telegramChatUrl,
    setTelegramChatUrl,
    inviteText,
    setInviteText,
    slogan,
    setSlogan,
    program,
    setProgram,
    status,
    setStatus,
    publishAt,
    setPublishAt,
    visibility,
    setVisibility,
    featured,
    setFeatured,
    genderRestriction,
    setGenderRestriction,
    minAge,
    setMinAge,
    maxAge,
    setMaxAge,
    selectedCategoryIds,
    setSelectedCategoryIds,
    homeGroupId,
    setHomeGroupId,
    requiresRsvpConfirmation,
    setRequiresRsvpConfirmation,
    homeGroups,
    isUploadingImage,
    isUploadingBannerImage,
    isUploadingVideo,
    videoUploadProgress,
    imageCacheBust,
    bannerCacheBust,
    editingTicketType,
    setEditingTicketType,
    serviceFeeConfigId,
    setServiceFeeConfigId,
    serviceFeePrices,
    setServiceFeePrices,
    showStatusPicker,
    setShowStatusPicker,
    showVisibilityPicker,
    setShowVisibilityPicker,
    showGenderPicker,
    setShowGenderPicker,
    showHomeGroupPicker,
    setShowHomeGroupPicker,
    editingField,
    setEditingField,
    setLastSavedAt,
    EVENT_STATUS_CONFIG: EVENT_STATUS_CONFIG,
    EVENT_VISIBILITY_CONFIG: EVENT_VISIBILITY_CONFIG,
    GENDER_CONFIG: GENDER_CONFIG,
  };
}
